/*
 * Copyright Mimic Networks, Inc. 2026.
 */

import { Form } from 'antd';

import { Node } from '@/client';
import { IncomingData } from '@/components/Node/IncomingData';
import { NodeViewPageCard } from '@/components/Node/NodeViewPageCard';
import { useMimicTranslation } from '@/hooks/useMimicTranslation';
import { Col } from '@/primitives/Col';
import { Row } from '@/primitives/Row';
import { Text } from '@/primitives/Text';
import { getActiveDirectoryProfile } from '@/utils/isDomainController';

export type NodeActiveDirectoryProps = {
  node: Node;
  isPending: boolean;
};

function formatLabel(str: string): string {
  return str.charAt(0).toUpperCase() + str.slice(1).replace(/_/g, ' ');
}

function formatScalarValue(value: unknown, yesLabel: string, noLabel: string): string | undefined {
  if (typeof value === 'boolean') {
    return value ? yesLabel : noLabel;
  }
  if (typeof value === 'string') {
    return value;
  }
  if (typeof value === 'number') {
    return String(value);
  }
  return undefined;
}

function ArrayValue({ items }: { items: string[] }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      {items.map((item) => (
        <Text key={item} mono ellipsis={{ tooltip: { overlayStyle: { maxWidth: 'none', whiteSpace: 'nowrap' } } }}>
          {item}
        </Text>
      ))}
    </div>
  );
}

export function NodeActiveDirectory({ node, isPending }: NodeActiveDirectoryProps) {
  const { t } = useMimicTranslation('node');

  const activeDirectoryProfile = getActiveDirectoryProfile(node);

  if (!activeDirectoryProfile) {
    return null;
  }

  const properties = activeDirectoryProfile.properties as Record<string, unknown> | undefined;
  const propertyEntries = properties ? Object.entries(properties) : [];

  return (
    <NodeViewPageCard title={t('active_directory')} dataTestId="active-directory-section">
      <Form layout="vertical">
        <Row gutter={[16, 16]}>
          {propertyEntries.map(([key, value]) => (
            <Col span={12} key={key}>
              {Array.isArray(value) ? (
                <IncomingData
                  label={t(key as Parameters<typeof t>[0], { defaultValue: formatLabel(key) })}
                  loading={isPending}
                  mono
                >
                  <ArrayValue items={value.map(String)} />
                </IncomingData>
              ) : (
                <IncomingData
                  label={t(key as Parameters<typeof t>[0], { defaultValue: formatLabel(key) })}
                  loading={isPending}
                  text={formatScalarValue(value, t('yes'), t('no'))}
                  mono
                />
              )}
            </Col>
          ))}
        </Row>
      </Form>
    </NodeViewPageCard>
  );
}
